import * as Yup from "yup";
import React, { FunctionComponent, useState } from "react";
import styles from "./SimpleContactForm.module.scss";
import SimpleContactFormBlockProps from "./SimpleContactFormProps.interface";

import BackgroundMedia from "components/BackgroundMedia/BackgroundMedia";
import combineClasses from "helpers/combine-classes";
import getWrapperClasses from "blocks/helpers/getWrapperClasses";
import useAnchor from "blocks/hooks/useAnchor";
import useSplitBackground from "blocks/hooks/useSplitBackground";
import useWrapperStyles from "blocks/hooks/useWrapperStyles";
import { Formik, Field, Form, ErrorMessage } from "formik";
import sendContactForm from "./helpers/sendContactForm";
import Button from "components/Button/Button";

const SimpleContactForm: FunctionComponent<SimpleContactFormBlockProps> = (props): JSX.Element => {
    const wrapperClasses = getWrapperClasses(props.settings);
    const wrapperStyles = useWrapperStyles(props.settings);

    const identifier = useAnchor(props.settings.anchorMenuLabel);
    const { splitStyles } = useSplitBackground(props);

    const [isMessageSuccess, setIsMessageSuccess] = useState<boolean>(false);
    const [isFormSubmitFailed, setIsFormSubmitFailed] = useState<boolean>(false);
    const [isLoading, setIsLoading] = useState<boolean>(false);

    return (
        <section className={combineClasses(styles.wrapper, ...wrapperClasses)} style={{ ...wrapperStyles, ...splitStyles }} id={identifier}>

            <div className={styles.container}>
                <Formik
                    initialValues={{ name: "", email: "", subject: "", honeypot: "" }}
                    validationSchema={Yup.object({
                        name: Yup.string().required("Enter your name"),
                        email: Yup.string().email("Enter valid email").required("Enter your email"),
                        subject: Yup.string()
                            .required("Enter message")
                            .max(500, "Message must be less than 500 characters long"),
                    })}
                    onSubmit={(values, { setSubmitting, resetForm }) => {
                        if (values.honeypot === "") {

                            // Replace newline characters with <br> tags
                            const messageWithLineBreaks = values.subject.replace(/\n/g, "<br>");

                            setIsLoading(true);

                            sendContactForm(
                                values.name,
                                values.email,
                                messageWithLineBreaks,
                                window.location.href
                            ).then((response: any) => {
                                setSubmitting(true);

                                if (response.status === 200) {
                                    setIsMessageSuccess(true);
                                    setSubmitting(false);
                                    resetForm();
                                }
                                else {
                                    setIsFormSubmitFailed(true);
                                    resetForm();
                                }
                            });
                        }
                    }}
                >
                    {({ isValidating, isSubmitting, errors, touched }) => {
                        const nameAlertClass = errors.name && touched.name ? styles.alert : "";
                        const emailAlertClass = errors.email && touched.email ? styles.alert : "";
                        const subjectAlertClass = errors.subject && touched.subject ? styles.alert : "";
                        return (
                            <Form className={styles.form}>
                                <>
                                    {/* Name */}
                                    <div className={styles.field_outer_wrapper}>
                                        <div className={styles.field_inner_wrapper}>
                                            <label
                                                htmlFor="name"
                                                className={styles.label}
                                            >
                                                {props?.content?.nameLabel || "Name"}
                                            </label>
                                            <Field
                                                name="name"
                                                id="name"
                                                type="text"
                                                placeholder="Name"
                                                className={combineClasses(styles.text_field, emailAlertClass)}
                                            />
                                        </div>
                                        {nameAlertClass ? (
                                            <div className={styles.alert_message}>
                                                <ErrorMessage name="name" />
                                            </div>
                                        ) : null}
                                    </div>

                                    {/* Email */}
                                    <div className={styles.field_outer_wrapper}>
                                        <div className={styles.field_inner_wrapper}>
                                            <label
                                                htmlFor="email"
                                                className={styles.label}
                                            >
                                                {props?.content?.emailLabel || "Email"}
                                            </label>
                                            <Field
                                                name="email"
                                                id="email"
                                                type="text"
                                                placeholder="Email"
                                                className={combineClasses(styles.text_field, emailAlertClass)}
                                            />
                                        </div>
                                        {emailAlertClass ? (
                                            <div className={styles.alert_message}>
                                                <ErrorMessage name="email" />
                                            </div>
                                        ) : null}
                                    </div>

                                    {/* Subject */}
                                    <div className={styles.field_outer_wrapper}>
                                        <div className={styles.field_inner_wrapper}>
                                            <label
                                                htmlFor="subject"
                                                className={styles.label}
                                            >
                                                {props?.content?.messageLabel || "Message"}
                                            </label>
                                            <Field
                                                as="textarea"
                                                name="subject"
                                                rows="4"
                                                id="subject"
                                                type="text"
                                                placeholder="Message"
                                                className={combineClasses(styles.text_field, emailAlertClass)}
                                            />
                                        </div>
                                        {subjectAlertClass ? (
                                            <div className={styles.alert_message}>
                                                <ErrorMessage name="subject" />
                                            </div>
                                        ) : null}
                                    </div>

                                    {/* Honeypot */}
                                    <div className={styles.field_outer_wrapper}>
                                        <div className={styles.field_inner_wrapper}>
                                            <label htmlFor="honeypot" className={styles.hidden_label}>
                                            </label>
                                            <Field
                                                type="text"
                                                name="honeypot"
                                                id="honeypot"
                                                className={styles.hidden_field}
                                            />
                                        </div>
                                    </div>

                                    {/* Submit button */}
                                    {props.content.buttons && props.content.buttons.length > 0 ? (
                                        <div className={styles.buttons}>
                                            {props.content.buttons.map((button: any) => (
                                                <Button type="submit" key={button.contentUdi} to={button.content.link} className={styles.button} variant={button.content.variant} {...button.content}>{button.content.label}</Button>
                                            ))}
                                        </div>
                                    ) : null}

                                    {/* Loading */}
                                    {isSubmitting ? (
                                        <div className={styles.app_loading_spinner}></div>
                                    ) : null}

                                    <div className={styles.form_message}>
                                        {isMessageSuccess ? (
                                            <p className={styles.form_success}>{props?.content?.simpleFormSuccess}</p>
                                        ) : null}

                                        {isFormSubmitFailed ? (
                                            <p className={styles.form_error}>{props?.content?.simpleFormError}</p>
                                        ) : null}
                                    </div>
                                </>
                            </Form>
                        );
                    }}
                </Formik>


            </div>

            {props.settings.backgroundMedia?.[0] && props.settings.backgroundMedia.length > 0 ? (
                <BackgroundMedia key={props.contentUdi} {...props.settings.backgroundMedia[0]} />
            ) : null}
        </section>
    );
};

export default SimpleContactForm;